// NOTICE: KAPTION-INTEGRATION
import { computed, unref } from 'vue';

const formatLabel = key =>
  key.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

const CONTACT_VARIABLES = [
  {
    key: 'contact.name',
    label: 'Contact Name',
    description: 'Full name of the contact',
  },
  {
    key: 'contact.first_name',
    label: 'Contact First Name',
    description: 'First name derived from contact name',
  },
  {
    key: 'contact.last_name',
    label: 'Contact Last Name',
    description: 'Last name derived from contact name',
  },
  {
    key: 'contact.email',
    label: 'Contact Email',
    description: 'Primary email associated with the contact',
  },
  {
    key: 'contact.phone_number',
    label: 'Contact Phone Number',
    description: 'Phone number in international format',
  },
];

const ACCOUNT_VARIABLES = [
  {
    key: 'account.name',
    label: 'Account Name',
    description: 'Name of the current Chatwoot account',
  },
];

const INBOX_VARIABLES = [
  {
    key: 'inbox.name',
    label: 'Inbox Name',
    description: 'Current WhatsApp inbox name',
  },
];

const AGENT_VARIABLES = [
  {
    key: 'agent.name',
    label: 'Agent Name',
    description: 'Full name of the sending agent',
  },
  {
    key: 'agent.first_name',
    label: 'Agent First Name',
    description: 'First name of the sending agent',
  },
  {
    key: 'agent.last_name',
    label: 'Agent Last Name',
    description: 'Last name of the sending agent',
  },
  {
    key: 'agent.available_name',
    label: 'Agent Available Name',
    description: 'Display name configured on the agent profile',
  },
];

const CAMPAIGN_VARIABLES = [
  {
    key: 'campaign.title',
    label: 'Campaign Title',
    description: 'Title of the WhatsApp campaign',
  },
  {
    key: 'campaign.description',
    label: 'Campaign Description',
    description: 'Description configured for the campaign',
  },
  {
    key: 'campaign.message',
    label: 'Campaign Message',
    description: 'Plain text content of the campaign',
  },
  {
    key: 'campaign.scheduled_at',
    label: 'Campaign Scheduled Time',
    description: 'Timestamp for the scheduled run',
  },
  {
    key: 'campaign.display_id',
    label: 'Campaign Display ID',
    description: 'Human-friendly campaign identifier',
  },
];

const buildCustomAttributeVariables = contacts => {
  const attributeKeys = new Set();
  contacts.forEach(contact => {
    Object.keys(contact?.custom_attributes || {}).forEach(key =>
      attributeKeys.add(key)
    );
  });

  return Array.from(attributeKeys).map(key => ({
    key: `contact.custom_attribute.${key}`,
    label: `Contact ${formatLabel(key)}`,
    description: `Custom attribute "${key}" from contact profile`,
  }));
};

const buildCampaignMetadataVariables = metadata => {
  const metadataKeys = Object.keys(metadata || {});
  return metadataKeys.map(key => ({
    key: `campaign.metadata.${key}`,
    label: `Campaign ${formatLabel(key)}`,
    description: `Metadata field "${key}" injected into campaign`,
  }));
};

export default function useTemplateVariables(options = {}) {
  const sampleContactsRef = options.sampleContacts || computed(() => []);
  const campaignMetadataRef = options.campaignMetadata || computed(() => ({}));

  const customAttributeVariables = computed(() =>
    buildCustomAttributeVariables(unref(sampleContactsRef) || [])
  );
  const campaignMetadataVariables = computed(() =>
    buildCampaignMetadataVariables(unref(campaignMetadataRef) || {})
  );

  const categorizedSuggestions = computed(() => [
    {
      category: 'Contact',
      variables: CONTACT_VARIABLES,
    },
    {
      category: 'Contact Custom Attributes',
      variables: customAttributeVariables.value,
    },
    {
      category: 'Account',
      variables: ACCOUNT_VARIABLES,
    },
    {
      category: 'Inbox',
      variables: INBOX_VARIABLES,
    },
    {
      category: 'Agent',
      variables: AGENT_VARIABLES,
    },
    {
      category: 'Campaign',
      variables: [...CAMPAIGN_VARIABLES, ...campaignMetadataVariables.value],
    },
  ]);

  const flatSuggestions = computed(() =>
    categorizedSuggestions.value.flatMap(({ category, variables }) =>
      variables.map(variable => ({
        ...variable,
        category,
      }))
    )
  );

  const getSuggestionByKey = key =>
    flatSuggestions.value.find(suggestion => suggestion.key === key);

  return {
    categorizedSuggestions,
    variableSuggestions: flatSuggestions,
    getSuggestionByKey,
  };
}
